import React, { useState, useEffect, useMemo } from "react";
import { marked } from "marked";
import FlaggedIcon from "../../../../../assets/svg/flagged-icon";
import CloseIcon from "../../../../../assets/svg/close-icon";
import { getModalityIcon } from "../../../../Communications/components/CommunicationDetailsPanel/utils/constants";
import VerbafloLogoIcon from "../../../../../assets/images/verbaflo-logo.png";
import Dropdownv2 from "../../../../../components/Dropdown/Dropdownv2";
import TextArea from "../../../../../components/TextArea/TextArea";
import Button from "../../../../../components/Button/Button";
import { TESTING_CONFIG, REASON_OPTIONS } from "./utils/helpers";
import { isHTML } from "../../../../Communications/utils/functions";
import {
  processHtmlContent,
  markdownBreakFormatter,
} from "../../../../../utils/functions/formatters";
import AttachmentCarousel from "../../../../../components/Common/AttachmentCarousel";
import { AttachmentItem } from "../../../../../components/Common/AttachmentInput";
import { Attachment } from "../../../../Communications/utils/types";

interface FlagFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageContent?: string;
  modality?: string;
  onSubmit?: (data: { reason: string; additionalDetails: string }) => void;
  isSubmitting?: boolean;
  attachments?: Attachment[];
}

const FlagFormModal = ({
  open,
  onOpenChange,
  messageContent = "Lorem ipsum dolor, sit amet consectetur adipisicing elit. Eligendi, repellat!",
  modality = "widget",
  onSubmit,
  isSubmitting = false,
  attachments,
}: FlagFormModalProps) => {
  const [reason, setReason] = useState<string>("");
  const [otherReasonText, setOtherReasonText] = useState<string>("");

  useEffect(() => {
    if (!open) {
      setReason("");
      setOtherReasonText("");
    }
  }, [open]);

  // Process message content with markdown and HTML formatting
  const processedContent = useMemo(() => {
    if (!messageContent) return "";

    let content: string;

    const formattedMessage =
      markdownBreakFormatter(messageContent?.replace(/\\n/g, "\n")) ?? "";

    if (!isHTML(messageContent)) {
      content = String(marked(formattedMessage))?.replace(
        /<a /g,
        '<a target="_blank" rel="noopener noreferrer" class="!text-blue-500 !underline"',
      );
    } else {
      content = processHtmlContent(formattedMessage);
    }

    return content;
  }, [messageContent]);

  // Convert attachments to carousel items
  const carouselItems: AttachmentItem[] = useMemo(() => {
    if (!attachments || attachments.length === 0) return [];
    return attachments.map((attachment, index) => ({
      id: (attachment.s3_key as string) || String(index),
      file: null,
      name: attachment.file_name as string,
      mime: attachment.content_type as string,
      previewUrl: attachment.s3_url as string,
      status: "sent" as const,
    }));
  }, [attachments]);

  const modalityLabel = useMemo(() => {
    return modality === "widget"
      ? "VerbaChat"
      : modality === "messenger"
        ? "VerbaMessenger"
        : modality === "email"
          ? "VerbaMail"
          : modality === "call"
            ? "VerbaCall"
            : // : modality === "sms"
              //   ? "VerbaSMS"
              modality === "fb_messenger"
              ? "Facebook Messenger"
              : modality === "wechat"
                ? "WeChat"
                : modality === "instagram"
                  ? "Instagram DMs"
                  : "VerbaChat";
  }, [modality]);

  const renderAttachments = () => {
    if (!attachments || attachments.length === 0) return null;

    return (
      <AttachmentCarousel
        items={carouselItems}
        onPreview={() => {}}
        onRemove={() => {}}
        hideDeleteButton={true}
      />
    );
  };

  const handleSubmit = () => {
    if (!reason) {
      return;
    }

    if (onSubmit) {
      onSubmit({
        reason,
        additionalDetails: otherReasonText,
      });
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/80 z-1000"
        onClick={() => onOpenChange(false)}
      />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-1001 rounded-lg shadow-lg min-w-[300px] max-w-[90vw] max-h-[90vh] overflow-auto py-2 bg-pure-white dark:bg-pure-white-dk"
      >
        <div className="px-6 py-0 mt-4 flex items-center justify-between">
          <div className="flex flex-col gap-2 justify-between">
            <div className="flex items-center gap-2">
              <FlaggedIcon className="w-[20px] h-[20px] text-verba-red-alert dark:text-verba-red-alert-dk" />
            </div>
          </div>
          <button
            className="shrink-0 w-[15px] h-[15px] bg-gray-100 dark:bg-tag-gray-dk rounded-full flex flex-col items-center justify-center transition-transform duration-200 hover:scale-110"
            aria-label="Close modal"
            onClick={() => onOpenChange(false)}
          >
            <CloseIcon className="w-[5.5px] h-[5.5px] text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
        </div>
        <div className="px-6 w-[324px] mb-5">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col">
              <h3 className="text-md font-semibold text-heading-text dark:text-heading-text-dk">
                {TESTING_CONFIG.FLAG_MODAAL.HEADER.TITLE}
              </h3>
              <span className="text-sm font-normal text-dark-gray dark:text-dark-gray-dk">
                {TESTING_CONFIG.FLAG_MODAAL.HEADER.DESCRIPTION}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <div className="bg-verba-chat-bubble dark:bg-verba-chat-bubble-dk rounded-lg p-4 flex flex-col gap-2">
                <div
                  className="
                    text-xs font-normal text-heading-text dark:text-heading-text-dk
                    break-words overflow-wrap-anywhere line-clamp-2
                    [&_*]:break-words [&_*]:overflow-wrap-anywhere
                    [&_p]:mb-0.5 [&_p:last-child]:mb-0
                    [&_ul]:mb-0.5 [&_ol]:mb-0.5
                    [&_h1]:mb-0.5 [&_h2]:mb-0.5 [&_h3]:mb-0.5 [&_h4]:mb-0.5 [&_h5]:mb-0.5 [&_h6]:mb-0.5
                    [&_code]:bg-gray-100 [&_code]:dark:bg-gray-800 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xxs
                    [&_blockquote]:mb-0.5 [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:dark:border-gray-600 [&_blockquote]:pl-2 [&_blockquote]:italic
                    [&_pre]:mb-0.5 [&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_pre]:overflow-wrap-anywhere [&_pre]:bg-gray-100 [&_pre]:dark:bg-gray-800 [&_pre]:p-2 [&_pre]:rounded [&_pre]:text-xxs
                    [&_pre>code]:bg-transparent [&_pre>code]:p-0
                  "
                  dangerouslySetInnerHTML={{
                    __html: processedContent,
                  }}
                />
                {attachments && attachments.length > 0 && (
                  <div className="flex flex-col gap-1">{renderAttachments()}</div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <span>{getModalityIcon(modality)}</span>
                  <span
                    className={`text-xxs font-normal text-heading-text dark:text-heading-text-dk ${modality === "sms" ? "mt-0.5" : ""}`}
                  >
                    {modalityLabel}
                  </span>
                </div>
                <div className="flex items-center gap-[2px]">
                  <img
                    src={VerbafloLogoIcon}
                    alt="Verbaflo Logo"
                    className="w-[18px] h-[18px]"
                  />
                  <p className="text-verba-indigo dark:text-verba-indigo-dk text-xs font-bold">
                    VerbaFlo.
                  </p>
                </div>
              </div>
            </div>

            <hr className="border-border-gray dark:border-border-gray-dk" />

            <div className="flex flex-col gap-[18px]">
              <Dropdownv2
                options={REASON_OPTIONS}
                value={reason}
                onChange={(value) => {
                  setReason(value);
                }}
                label="Choose a reason"
                labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
                placeholder="Select"
                triggerClassName="w-[275px]"
                selectedOptionContainerClassName="w-full"
                placeholderClassName="text-md text-gray-placeholder dark:text-gray-placeholder-dk !font-normal"
                dropdownClassName="bg-pure-white dark:bg-pure-white-dk !max-h-[200px]"
                optionClassName="border-b border-border-gray dark:border-border-gray-dk pb-2"
              />
              <div className="flex flex-col">
                <label className="text-md text-verba-field-label dark:text-verba-field-label-dk">
                  Tell us more{" "}
                  <span className="text-xs not-even:text-gray-placeholder dark:text-gray-placeholder-dk">
                    (optional)
                  </span>
                </label>
                <TextArea
                  value={otherReasonText}
                  onChange={(e) => {
                    setOtherReasonText(e.target.value);
                  }}
                  rows={4}
                  placeholder="What went wrong? Any specific examples or suggestions?"
                  secondaryContainerClassName="border border-border-gray dark:border-border-gray-dk rounded-lg p-2"
                />
              </div>

              <div className="flex items-center justify-end gap-2 mt-4">
                <Button
                  innerText="Cancel"
                  buttonContainerClass="px-7.5 py-1 rounded-md bg-verba-indigo dark:bg-verba-indigo-dk rounded-md transition-all hover:opacity-90
                  text-pure-white dark:text-pure-white-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:dark:text-verba-indigo-dk"
                  innerTextClass="text-md font-medium"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                />
                <Button
                  innerText={isSubmitting ? "Submitting..." : "Submit Feedback"}
                  buttonContainerClass={`px-4 py-1 rounded-md border border-verba-red-alert dark:border-verba-red-alert-dk
                  text-verba-red-alert dark:text-verba-red-alert-dk hover:text-pure-white  hover:bg-verba-red-alert dark:hover:bg-verba-red-alert-dk
                  disabled:hover:bg-transparent disabled:hover:text-verba-red-alert disabled:hover:dark:text-verba-red-alert-dk
                  ${
                    !reason || isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  innerTextClass="text-md font-medium whitespace-nowrap"
                  onClick={handleSubmit}
                  disabled={!reason || isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FlagFormModal;
